import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import FrequentAskedQuestions from 'components/FrequentAskedQuestions';
import FeaturedImage from 'components/Landings/FeaturedImage';
import Layout from 'components/Layout';
import LazyHydration from 'components/LazyHydration';
import PageDescription from 'components/PageDescription';

import { getCategoriesLandings } from 'lib/landings';
import { toLandingPageLink } from 'lib/links';
import { offerQueryKeys } from 'lib/queryKeys';

import { getLandingOffersList } from 'services/landing';

import CategoriesCarousel from '../../Landings/CategoriesCarousel';
import Heading from './Heading';
import Sidebar from './Sidebar';

import LANDINGS from 'constants/landings';

const OffersList = dynamic(() => import('components/Timeline/OffersList'));

const Landing = ({
  initialOffers = { offers: [] },
  offersListProps = {},
  categoryOrSubcategorySlug = {},
  landing: {
    landingId,
    landingCategories,
    landingDescription,
    landingEventCategory,
    landingFaqs,
    landingImage: { image, imageMobile, imageBgColors, imageLongUrl },
    landingMetaDescription,
    landingMetaTitle,
    landingName,
    landingSlug,
    landingText,
    landingTitle,
  },
  sort,
  storesWithFeaturedCoupons = [],
}) => {
  const { asPath } = useRouter();

  const gaEventCategory = landingEventCategory || landingName;

  const homeLandingItem = {
    id: landingId,
    name: landingName,
    slug: landingSlug,
    href: toLandingPageLink(landingSlug, LANDINGS.TYPES.SEASONAL),
    active: asPath.endsWith(`/${landingSlug}/`),
  };

  const orderedCategories = useMemo(() => {
    return getCategoriesLandings({
      landingCategories,
      asPath,
      landingSlug,
      homeLandingItem,
    });
  }, [asPath]);

  return (
    <>
      <NextSeo description={landingMetaDescription} title={landingMetaTitle} />
      {(image || imageMobile) && (
        <FeaturedImage
          bgcolor={imageBgColors}
          gaEventCategory={landingEventCategory}
          image={image}
          mobileImage={imageMobile}
          url={imageLongUrl}
        />
      )}
      {landingCategories && (
        <CategoriesCarousel
          className="mx-auto mt-8 w-full lg:px-2 xl:w-screen-xl"
          gaEventCategory={gaEventCategory}
          orderedCategories={orderedCategories}
        />
      )}
      <Layout
        gridProps={{ className: 'mb-18' }}
        headingComponent={
          <Heading
            breadcrumbActiveItem={landingName}
            description={landingDescription}
            isBreadcrumbActive={initialOffers.offers.length > 0}
            isDescriptionInitiallyCollapsed={initialOffers.offers.length > 0}
            title={landingTitle}
          />
        }
        sidebarComponent={
          <Sidebar
            gaEventCategory={landingEventCategory}
            storesWithFeaturedCoupons={storesWithFeaturedCoupons}
          />
        }
      >
        {initialOffers.offers.length > 0 && (
          <OffersList
            className="mt-0"
            gaEventCategory={landingEventCategory}
            initialOffers={initialOffers}
            queryFn={({ after }) =>
              getLandingOffersList({
                after,
                landingSlug,
                sort,
                ...categoryOrSubcategorySlug,
              })
            }
            queryKey={offerQueryKeys.listByLanding({
              landingId,
              categoryOrSubcategorySlug,
            })}
            paginationCappedAtFinishedOffer
            {...offersListProps}
          />
        )}
        {landingText &&
          (initialOffers.offers.length > 0 ? (
            <LazyHydration placeholderSize={{ height: 300 }}>
              <PageDescription
                className="bg-transparent dark:bg-transparent"
                pageDescription={landingText}
              />
            </LazyHydration>
          ) : (
            <PageDescription
              className="bg-transparent dark:bg-transparent"
              pageDescription={landingText}
            />
          ))}
        {landingFaqs && landingFaqs.length > 0 && (
          <FrequentAskedQuestions className="mt-6" faqs={landingFaqs} />
        )}
      </Layout>
    </>
  );
};

Landing.propTypes = {
  initialOffers: PropTypes.shape({
    after: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sort: PropTypes.string,
    before: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string.isRequired,
        offerComments: PropTypes.number.isRequired,
        offerId: PropTypes.number.isRequired,
        offerIsHighlight: PropTypes.bool.isRequired,
        ratings: PropTypes.shape().isRequired,
        offerOldPrice: PropTypes.number,
        offerPhoto: PropTypes.string.isRequired,
        offerPrice: PropTypes.number.isRequired,
        offerPriceType: PropTypes.string.isRequired,
        offerPublished: PropTypes.string.isRequired,
        offerSlug: PropTypes.string.isRequired,
        offerStatusName: PropTypes.string.isRequired,
        offerUserVisibility: PropTypes.string.isRequired,
        offerTags: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
          })
        ),
        offerTitle: PropTypes.string.isRequired,
        storeDomain: PropTypes.string.isRequired,
      }).isRequired
    ).isRequired,
  }),
  offersListProps: PropTypes.shape(),
  landing: PropTypes.shape({
    landingCategories: PropTypes.shape({
      categories: PropTypes.arrayOf(
        PropTypes.shape({
          categoryId: PropTypes.number.isRequired,
          categoryName: PropTypes.string.isRequired,
          categorySlug: PropTypes.string.isRequired,
        })
      ),
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          subcategoryId: PropTypes.number.isRequired,
          subcategoryName: PropTypes.string.isRequired,
          subcategorySlug: PropTypes.string.isRequired,
        })
      ),
    }),
    landingDescription: PropTypes.string.isRequired,
    landingEventCategory: PropTypes.string,
    landingFaqs: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string.isRequired,
        answer: PropTypes.string.isRequired,
      })
    ),
    landingImage: PropTypes.shape({
      image: PropTypes.string,
      imageMobile: PropTypes.string,
      imageBgColors: PropTypes.arrayOf(PropTypes.string),
      imageUrl: PropTypes.string,
      imageLongUrl: PropTypes.string,
    }),
    landingName: PropTypes.string.isRequired,
    landingMetaDescription: PropTypes.string.isRequired,
    landingMetaTitle: PropTypes.string.isRequired,
    landingSlug: PropTypes.string.isRequired,
    landingText: PropTypes.string.isRequired,
    landingTitle: PropTypes.string.isRequired,
    landingType: PropTypes.string.isRequired,
  }).isRequired,
  storesWithFeaturedCoupons: PropTypes.arrayOf(
    PropTypes.shape({
      storeName: PropTypes.string,
      storeSlug: PropTypes.string,
    }).isRequired
  ),
};

export default Landing;
